import { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAdminPermissions, type PermissionKey } from '@/hooks/useAdminPermissions';
import { supabase } from '@/integrations/supabase/client';
import MFAEnroll from '@/components/admin/MFAEnroll';
import MFAChallenge from '@/components/admin/MFAChallenge';
import EmailOTPChallenge from '@/components/admin/EmailOTPChallenge';
import MFAMethodSelector, { type MFAMethod } from '@/components/admin/MFAMethodSelector';

async function logMfaEvent(method: string, eventType: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get display name from user_roles
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('display_name, email')
      .eq('user_id', user.id)
      .maybeSingle();

    await supabase.from('admin_mfa_audit_log').insert({
      user_id: user.id,
      email: roleData?.email || user.email || null,
      display_name: roleData?.display_name || null,
      mfa_method: method,
      event_type: eventType,
      user_agent: navigator.userAgent,
    });
  } catch (err) {
    console.error('Failed to log MFA event:', err);
  }
}

interface AdminRouteProps {
  children: React.ReactNode;
  permission?: PermissionKey;
}

const routePermissionMap: Record<string, PermissionKey> = {
  '/admin': 'dashboard',
  '/admin/templates': 'templates',
  '/admin/orders': 'orders',
  '/admin/customers': 'customers',
  '/admin/invoices': 'invoices',
  '/admin/products': 'products',
  '/admin/seo': 'seo',
  '/admin/coupons': 'coupons',
  '/admin/webhooks': 'webhooks',
  '/admin/settings': 'settings',
  '/admin/funnel': 'funnel',
  '/admin/tests': 'e2e_tests',
};

// Module-level MFA cache so re-mounts don't trigger full re-check
let mfaCache: { userId: string; status: 'verified' } | null = null;

export default function AdminRoute({ children, permission }: AdminRouteProps) {
  const { user, loading, isAdmin, signOut, mfaRequired } = useAuth();
  const { hasPermission, isLoading: permLoading } = useAdminPermissions();
  const location = useLocation();
  const [mfaStatus, setMfaStatus] = useState<'loading' | 'verified' | 'needs_enroll' | 'needs_challenge' | 'select_method'>(() => {
    // If we already verified MFA for this user, skip loading
    if (user && mfaCache?.userId === user.id && mfaCache.status === 'verified') {
      return 'verified';
    }
    return 'loading';
  });
  const [availableMethods, setAvailableMethods] = useState<MFAMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<MFAMethod | null>(null);

  const setMfaVerified = useCallback(() => {
    if (user) mfaCache = { userId: user.id, status: 'verified' };
    setMfaStatus('verified');
  }, [user]);

  const checkMFA = useCallback(async () => {
    // Already cached as verified for this user — skip
    if (user && mfaCache?.userId === user.id && mfaCache.status === 'verified') {
      setMfaStatus('verified');
      return;
    }

    setMfaStatus('loading');

    if (!user) {
      setMfaStatus('needs_enroll');
      return;
    }

    if (!mfaRequired) {
      setSelectedMethod(null);
      setAvailableMethods([]);
      setMfaVerified();
      return;
    }
    
    const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    
    if (aalError) {
      setMfaStatus('needs_enroll');
      return;
    }

    if (aalData.currentLevel === 'aal2') {
      setMfaVerified();
      return;
    }

    // AAL1 — check available methods
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const verifiedTOTP = factors?.totp?.filter(f => f.status === 'verified') ?? [];
    const hasTOTP = verifiedTOTP.length > 0;

    // Check email OTP setting
    const { data: emailOtpSetting } = await supabase
      .from('shop_settings')
      .select('value')
      .eq('key', 'admin_mfa_email_otp_enabled')
      .maybeSingle();
    
    const hasEmailOTP = emailOtpSetting?.value === true || (emailOtpSetting?.value as any)?.enabled === true;

    const methods: MFAMethod[] = [];
    if (hasTOTP) methods.push('totp');
    if (hasEmailOTP) methods.push('email');

    setAvailableMethods(methods);

    if (methods.length === 0) {
      setMfaStatus('needs_enroll');
    } else if (methods.length === 1) {
      setSelectedMethod(methods[0]);
      setMfaStatus('needs_challenge');
    } else {
      setMfaStatus('select_method');
    }
  }, [mfaRequired, user, setMfaVerified]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    checkMFA();
  }, [user, isAdmin, checkMFA]);

  const handleMFACancel = async () => {
    mfaCache = null;
    await signOut();
  };

  const handleSwitchMethod = () => {
    setSelectedMethod(null);
    setMfaStatus('select_method');
  };

  if (loading || permLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Zugriff verweigert</h1>
          <p className="text-muted-foreground">Du hast keine Admin-Berechtigung.</p>
        </div>
      </div>
    );
  }

  // MFA enforcement
  if (mfaStatus === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (mfaStatus === 'needs_enroll') {
    return (
      <MFAEnroll
        onEnrolled={() => {
          logMfaEvent('totp', 'enroll');
          setMfaVerified();
        }}
        onCancelled={handleMFACancel}
      />
    );
  }

  if (mfaStatus === 'select_method') {
    return (
      <MFAMethodSelector
        availableMethods={availableMethods}
        onSelect={(method) => {
          setSelectedMethod(method);
          setMfaStatus('needs_challenge');
        }}
        onCancel={handleMFACancel}
      />
    );
  }

  if (mfaStatus === 'needs_challenge') {
    if (selectedMethod === 'email') {
      return (
        <EmailOTPChallenge
          onVerified={() => {
            logMfaEvent('email', 'login');
            setMfaVerified();
          }}
          onCancel={handleMFACancel}
          onSwitchMethod={availableMethods.length > 1 ? handleSwitchMethod : undefined}
          hasOtherMethods={availableMethods.length > 1}
        />
      );
    }
    return (
      <MFAChallenge
        onVerified={() => {
          logMfaEvent('totp', 'login');
          setMfaVerified();
        }}
        onCancel={handleMFACancel}
        onSwitchMethod={availableMethods.length > 1 ? handleSwitchMethod : undefined}
        hasOtherMethods={availableMethods.length > 1}
      />
    );
  }

  // Check route-level permission
  const requiredPerm = permission || Object.entries(routePermissionMap).find(
    ([path]) => location.pathname === path || location.pathname.startsWith(path + '/')
  )?.[1];

  if (requiredPerm && !hasPermission(requiredPerm)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Kein Zugriff</h1>
          <p className="text-muted-foreground">Du hast keine Berechtigung für diesen Bereich.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
